(() => {
  /*
    EDCB Product Template JS

    Data attributes (from section):
      - data-EDCB-product-section: root scope for this product section.
      - data-EDCB-product-json: JSON with product/variants/media.
      - data-EDCB-main-image: main product image element.
      - data-EDCB-zoom: container used by jquery.zoom.
      - data-EDCB-thumb: thumbnail buttons with image metadata.
      - data-EDCB-swatch: swatch option buttons (if enabled).
      - data-EDCB-option-select: dropdown option selectors (if swatches disabled).
      - data-EDCB-form: add-to-cart form element.
      - data-EDCB-price / data-EDCB-compare: price targets.
      - data-EDCB-klarna: Klarna text element.
      - data-EDCB-klarna-button: button(s) that open the Klarna popup.
      - data-EDCB-klarna-modal: Klarna modal container.

    Section settings (dataset on root):
      - edcbInitialVariantId: initial variant fallback.
      - edcbZoomEnabled: enable/disable zoom.
      - edcbVariantsEnabled: enable/disable variant UI.
      - edcbSwatchesEnabled: swatches vs dropdowns.
      - edcbAtcText: custom ATC button text.
      - edcbKlarnaCustom: use custom Klarna text (richtext).
      - edcbKlarnaPopup: enable popup behavior.

    Behavior:
      - Syncs variant selection to price, availability, image, and Klarna text.
      - Re-initializes zoom after the main image updates.
      - Uses Klarna modal with overlay when enabled.
  */
  const sections = document.querySelectorAll('[data-EDCB-product-section]');

  sections.forEach((section) => {
    // Parse product JSON for variants and media.
    const productJsonEl = section.querySelector('[data-EDCB-product-json]');
    if (!productJsonEl) return;

    let productData = null;
    try {
      productData = JSON.parse(productJsonEl.textContent);
    } catch (error) {
      console.error('EDCB: Failed to parse product JSON.', error);
      return;
    }

    // Core DOM references.
    const mainImage = section.querySelector('[data-EDCB-main-image]');
    const zoomTarget = section.querySelector('[data-EDCB-zoom]');
    const priceEl = section.querySelector('[data-EDCB-price]');
    const compareEl = section.querySelector('[data-EDCB-compare]');
    const klarnaEl = section.querySelector('[data-EDCB-klarna]');
    const klarnaButtons = Array.from(section.querySelectorAll('[data-EDCB-klarna-button]'));
    let klarnaModal = section.querySelector('[data-EDCB-klarna-modal]');
    const qtyInput = section.querySelector('[data-EDCB-qty-input]');
    const qtyMinus = section.querySelector('[data-EDCB-qty-minus]');
    const qtyPlus = section.querySelector('[data-EDCB-qty-plus]');
    const atcButton = section.querySelector('[data-EDCB-atc]');
    const variantIdInput = section.querySelector('[data-EDCB-variant-id]');
    const messageEl = section.querySelector('[data-EDCB-message]');
    const thumbs = Array.from(section.querySelectorAll('[data-EDCB-thumb]'));
    const form = section.querySelector('[data-EDCB-form]');
    const swatches = Array.from(section.querySelectorAll('[data-EDCB-swatch]'));
    const optionSelects = Array.from(section.querySelectorAll('[data-EDCB-option-select]'));
    // Section settings passed as data attributes.
    const zoomEnabled = section.dataset.edcbZoomEnabled !== 'false';
    const variantsEnabled = section.dataset.edcbVariantsEnabled !== 'false';
    const swatchesEnabled = section.dataset.edcbSwatchesEnabled !== 'false';
    const atcText = section.dataset.edcbAtcText || 'Add to cart';
    const initialVariantId = section.dataset.edcbInitialVariantId;
    const klarnaCustom = section.dataset.edcbKlarnaCustom === 'true';
    const klarnaPopup = section.dataset.edcbKlarnaPopup === 'true';

    const locale = section.dataset.locale || 'en-US';
    const currency = section.dataset.currency || 'USD';
    const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });

    const formatMoney = (cents) => formatter.format((cents || 0) / 100);

    const clearMessage = () => {
      if (!messageEl) return;
      messageEl.textContent = '';
      messageEl.classList.remove('is-success', 'is-error');
    };

    const setMessage = (text, type) => {
      if (!messageEl) return;
      messageEl.textContent = text;
      messageEl.classList.remove('is-success', 'is-error');
      if (type) messageEl.classList.add(type);
    };

    // Resolve selected options from swatches or selects.
    const getSelectedOptions = () => {
      const optionCount = productData.options ? productData.options.length : 0;
      const selections = new Array(optionCount).fill(null);
      if (swatchesEnabled && swatches.length) {
        swatches.forEach((swatch) => {
          if (!swatch.classList.contains('is-active')) return;
          const index = Number(swatch.dataset.optionIndex || 0);
          selections[index] = swatch.dataset.optionValue;
        });
      } else if (optionSelects.length) {
        optionSelects.forEach((selectEl) => {
          const index = Number(selectEl.dataset.optionIndex || 0);
          selections[index] = selectEl.value;
        });
      }
      return selections;
    };

    // Find matching variant based on UI state or initial fallback.
    const findVariant = () => {
      if (!variantsEnabled) {
        return (
          productData.variants.find(
            (variant) => String(variant.id) === String(initialVariantId)
          ) ||
          productData.variants.find((variant) => variant.available) ||
          productData.variants[0] ||
          null
        );
      }
      if (!swatchesEnabled && !optionSelects.length) {
        return (
          productData.variants.find(
            (variant) => String(variant.id) === String(initialVariantId)
          ) ||
          productData.variants.find((variant) => variant.available) ||
          productData.variants[0] ||
          null
        );
      }
      const selectedOptions = getSelectedOptions();
      return (
        productData.variants.find((variant) =>
          variant.options.every((option, index) => option === selectedOptions[index])
        ) || null
      );
    };

    let zoomInitialized = false;
    let pendingZoomSrc = '';
    // Remove size suffixes for non-files URLs (e.g., _1024x).
    const stripShopifySize = (pathValue) =>
      String(pathValue || '').replace(/_(\d+x\d*|\d+x|x\d+)(?=\.[^/.?]+$)/, '');
    // Normalize zoom URLs to original assets.
    const getZoomUrl = (value) => {
      if (!value) return '';
      try {
        const url = new URL(value, window.location.origin);
        if (url.searchParams.has('width')) {
          url.searchParams.delete('width');
        }
        if (!url.pathname.includes('/files/')) {
          url.pathname = stripShopifySize(url.pathname);
        }
        return url.toString();
      } catch (error) {
        const cleaned = String(value)
          .replace(/([?&])width=\d+(&|$)/, '$1')
          .replace(/[?&]$/, '');
        return cleaned.includes('/files/') ? cleaned : stripShopifySize(cleaned);
      }
    };
    const normalizeUrl = (value) => {
      if (!value) return '';
      try {
        return new URL(value, window.location.origin).href.split('?')[0];
      } catch (error) {
        return String(value).split('?')[0];
      }
    };
    // Defer zoom init to allow the main image to settle.
    const scheduleZoomInit = () => {
      if (!zoomEnabled) return;
      setTimeout(initZoom, 0);
      setTimeout(initZoom, 300);
    };
    // Wait for the main image to match a target before zooming.
    const scheduleZoomForSrc = (targetSrc) => {
      if (!zoomEnabled) return;
      if (!targetSrc || !mainImage) return;
      let resolvedTarget = targetSrc;
      try {
        resolvedTarget = new URL(targetSrc, window.location.origin).toString();
      } catch (error) {
        resolvedTarget = targetSrc;
      }
      const normalizedTarget = normalizeUrl(resolvedTarget);
      let attempts = 0;
      const maxAttempts = 12;
      const check = () => {
        attempts += 1;
        const current = normalizeUrl(mainImage.currentSrc || mainImage.src);
        if (current && current === normalizedTarget) {
          pendingZoomSrc = getZoomUrl(resolvedTarget);
          console.log('EDCB zoom: matched variant image, init', resolvedTarget);
          initZoom();
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(check, 150);
        } else {
          console.log('EDCB zoom: variant image mismatch, fallback init', {
            target: normalizedTarget,
            current
          });
          pendingZoomSrc = getZoomUrl(resolvedTarget);
          initZoom();
        }
      };
      setTimeout(check, 0);
    };
    // (Re)initialize the jQuery zoom plugin.
    const initZoom = () => {
      if (!zoomEnabled) return;
      if (!zoomTarget || !mainImage) return;
      if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.zoom) {
        setTimeout(initZoom, 150);
        return;
      }
      const $zoomTarget = window.jQuery(zoomTarget);
      $zoomTarget.trigger('zoom.destroy');
      $zoomTarget.find('.zoomImg').remove();
      $zoomTarget.removeData('zoom');
      const zoomSrc = pendingZoomSrc || getZoomUrl(mainImage.currentSrc || mainImage.src);
      console.log('EDCB zoom: init with src', zoomSrc);
      $zoomTarget.zoom({ url: zoomSrc, on: 'mouseover' });
      zoomInitialized = true;
    };

    // Update main image and reset zoom state.
    const setMainImage = (src, alt, zoomSrc) => {
      if (!mainImage || mainImage.tagName !== 'IMG' || !src) return;
      let resolvedSrc = src;
      try {
        resolvedSrc = new URL(src, window.location.origin).toString();
      } catch (error) {
        resolvedSrc = src;
      }
      zoomInitialized = false;
      pendingZoomSrc = getZoomUrl(zoomSrc || resolvedSrc);
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = resolvedSrc;
      if (alt) mainImage.alt = alt;
      const confirmImage = new Image();
      confirmImage.onload = () => {
        if (normalizeUrl(mainImage.src) === normalizeUrl(resolvedSrc)) scheduleZoomInit();
      };
      confirmImage.src = resolvedSrc;
      scheduleZoomInit();
      scheduleZoomForSrc(resolvedSrc);
    };

    const scrollThumbIntoView = (thumbItem) => {
      if (!thumbItem) return;
      const slider = section.querySelector('.EDCB-gallery__thumbs');
      if (slider && typeof slider.scrollTo === 'function') {
        const left = thumbItem.offsetLeft;
        slider.scrollTo({ left, behavior: 'smooth' });
      } else if (typeof thumbItem.scrollIntoView === 'function') {
        thumbItem.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
    };

    const setActiveThumbById = (id) => {
      if (!id || !thumbs.length) return null;
      let activeThumb = null;
      thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.imageId === String(id);
        const thumbItem = thumb.closest('.EDCB-gallery__thumb');
        if (thumbItem) {
          thumbItem.classList.toggle('is-active', isActive);
        }
        if (isActive) activeThumb = thumbItem || thumb;
      });
      scrollThumbIntoView(activeThumb);
      return activeThumb;
    };

    const setActiveThumbBySrc = (src) => {
      if (!src || !thumbs.length) return null;
      let activeThumb = null;
      thumbs.forEach((thumb) => {
        const thumbSrc = thumb.dataset.imageSrc || '';
        const isActive = thumbSrc.split('?')[0] === src.split('?')[0];
        const thumbItem = thumb.closest('.EDCB-gallery__thumb');
        if (thumbItem) {
          thumbItem.classList.toggle('is-active', isActive);
        }
        if (isActive) activeThumb = thumbItem || thumb;
      });
      scrollThumbIntoView(activeThumb);
      return activeThumb;
    };

    // Price rendering.
    const updatePrice = (variant) => {
      if (!variant || !priceEl) return;
      priceEl.textContent = formatMoney(variant.price);

      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
        }
      }
    };

    // Klarna text when not using custom content.
    const updateKlarna = (variant) => {
      if (!variant || !klarnaEl || klarnaCustom) return;
      const installment = Math.round(variant.price / 4);
      klarnaEl.textContent = `or 4 payments of ${formatMoney(installment)} with Klarna`;
    };

    // ATC button state.
    const updateAvailability = (variant) => {
      if (!atcButton) return;
      if (!variant || !variant.available) {
        atcButton.disabled = true;
        atcButton.textContent = 'Sold out';
        return;
      }

      atcButton.disabled = false;
      atcButton.textContent = atcText;
    };

    // Resolve variant image from featured image or media.
    const getVariantImageData = (variant) => {
      if (!variant) return null;
      const featuredImage = variant.featured_image;
      if (featuredImage) {
        if (typeof featuredImage === 'string') {
          return { src: featuredImage, alt: variant.title || '' };
        }
        if (featuredImage.src) {
          return {
            src: featuredImage.src,
            alt: featuredImage.alt || variant.title || '',
            id: featuredImage.id
          };
        }
      }
      const featuredMedia = variant.featured_media;
      const previewImage = featuredMedia && featuredMedia.preview_image;
      if (previewImage && previewImage.src) {
        return {
          src: previewImage.src,
          alt: previewImage.alt || variant.title || '',
          id: previewImage.id || featuredMedia.id
        };
      }
      return null;
    };

    // Sync image/thumbnail with the selected variant.
    const updateVariantImage = (variant) => {
      const imageData = getVariantImageData(variant);
      if (!imageData || !imageData.src) {
        console.log('EDCB zoom: missing variant image data', variant);
        return;
      }
      console.log('EDCB zoom: variant image selected', imageData);
      setMainImage(imageData.src, imageData.alt || variant.title, imageData.src);
      scheduleZoomForSrc(imageData.src);
      if (imageData.id) {
        const active = setActiveThumbById(imageData.id);
        if (!active) {
          setActiveThumbBySrc(imageData.src);
        }
      } else {
        setActiveThumbBySrc(imageData.src);
      }
    };

    const updateSwatchState = (optionIndex, value) => {
      swatches.forEach((swatch) => {
        if (Number(swatch.dataset.optionIndex) !== optionIndex) return;
        const isActive = swatch.dataset.optionValue === value;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    const applyVariantOptions = (variant) => {
      if (!variant || !Array.isArray(variant.options)) return;
      variant.options.forEach((value, index) => updateSwatchState(index, value));
    };

    const getVariantByImageId = (imageId) => {
      if (!imageId) return null;
      return (
        productData.variants.find((variant) => {
          const featuredImageId = variant.featured_image && variant.featured_image.id;
          const featuredMediaId =
            (variant.featured_media &&
              variant.featured_media.preview_image &&
              variant.featured_media.preview_image.id) ||
            (variant.featured_media && variant.featured_media.id);
          return String(featuredImageId || featuredMediaId) === String(imageId);
        }) || null
      );
    };

    // Push variant state into the UI.
    const syncVariantState = () => {
      const variant = findVariant();
      clearMessage();

      if (!variant) {
        updateAvailability(null);
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;
      updatePrice(variant);
      updateKlarna(variant);
      updateAvailability(variant);
      updateVariantImage(variant);
    };

    // Quantity input helpers.
    const adjustQuantity = (delta) => {
      if (!qtyInput) return;
      const current = parseInt(qtyInput.value, 10) || 1;
      const next = Math.max(1, current + delta);
      qtyInput.value = next;
    };

    if (swatchesEnabled) {
      swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => {
          const optionIndex = Number(swatch.dataset.optionIndex || 0);
          const value = swatch.dataset.optionValue;
          updateSwatchState(optionIndex, value);
          syncVariantState();
        });
      });
    }
    if (!swatchesEnabled && optionSelects.length) {
      optionSelects.forEach((selectEl) => {
        selectEl.addEventListener('change', () => {
          syncVariantState();
        });
      });
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.imageSrc;
        const alt = thumb.dataset.imageAlt || '';
        const imageId = thumb.dataset.imageId;
        const matchingVariant = getVariantByImageId(imageId);
        if (matchingVariant) {
          applyVariantOptions(matchingVariant);
          syncVariantState();
        } else {
          setMainImage(src, alt, src);
          setActiveThumbById(imageId);
        }
      });
    });

    if (qtyMinus) qtyMinus.addEventListener('click', () => adjustQuantity(-1));
    if (qtyPlus) qtyPlus.addEventListener('click', () => adjustQuantity(1));

    // Ajax add-to-cart handler.
    if (form) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        clearMessage();

        if (!variantIdInput || !variantIdInput.value) {
          setMessage('Please select a variant.', 'is-error');
          return;
        }

        if (atcButton) atcButton.disabled = true;

        try {
          const formData = new FormData(form);
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
            formData.append('sections', cartDrawer.getSectionsToRender().map((sectionInfo) => sectionInfo.id));
            formData.append('sections_url', window.location.pathname);
            if (typeof cartDrawer.setActiveElement === 'function') {
              cartDrawer.setActiveElement(document.activeElement);
            }
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData
          });

          const data = await response.json();

          if (!response.ok || data.status) {
            setMessage(data.description || 'Unable to add to cart.', 'is-error');
          } else if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
            setMessage('Added to cart.', 'is-success');
          } else {
            window.location = '/cart';
          }
        } catch (error) {
          console.error('EDCB: Add to cart failed.', error);
          setMessage('Something went wrong. Please try again.', 'is-error');
        } finally {
          if (atcButton) {
            const variant = findVariant();
            if (variant && variant.available) atcButton.disabled = false;
          }
        }
      });
    }

    // Klarna modal controls.
    const closeKlarnaModal = () => {
      if (!klarnaModal) return;
      klarnaModal.classList.remove('is-active');
      klarnaModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('EDCB-klarna-open');
    };
    const openKlarnaModal = () => {
      if (!klarnaModal) return;
      klarnaModal.classList.add('is-active');
      klarnaModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('EDCB-klarna-open');
    };
    if (klarnaModal && klarnaModal.parentElement !== document.body) {
      document.body.appendChild(klarnaModal);
    }
    if (klarnaButtons.length && klarnaModal && klarnaPopup) {
      klarnaButtons.forEach((button) => {
        button.addEventListener('click', openKlarnaModal);
      });
      klarnaModal.querySelectorAll('[data-EDCB-klarna-close]').forEach((btn) => {
        btn.addEventListener('click', closeKlarnaModal);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeKlarnaModal();
      });
    }

    const tabButtons = Array.from(section.querySelectorAll('[data-EDCB-tab-button]'));
    const tabPanels = Array.from(section.querySelectorAll('[data-EDCB-tab-panel]'));

    const activateTab = (targetId) => {
      tabButtons.forEach((button) => {
        button.classList.toggle('is-active', button.dataset.tabTarget === targetId);
      });

      tabPanels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.id === targetId);
      });
    };

    tabButtons.forEach((button) => {
      button.addEventListener('click', () => activateTab(button.dataset.tabTarget));
    });

    syncVariantState();
    if (zoomTarget && zoomEnabled) {
      zoomTarget.addEventListener('mouseenter', () => {
        if (!zoomInitialized) initZoom();
      });
      zoomTarget.addEventListener('touchstart', () => {
        if (!zoomInitialized) initZoom();
      });
    }
    if (mainImage && zoomEnabled) {
      mainImage.addEventListener('load', () => {
        pendingZoomSrc = getZoomUrl(mainImage.currentSrc || mainImage.src);
        zoomInitialized = false;
        scheduleZoomInit();
      });
      const observer = new MutationObserver(() => {
  /*
    EDCB Product Template JS

    Data attributes (from section):
      - data-EDCB-product-section: root scope for this product section.
      - data-EDCB-product-json: JSON with product/variants/media.
      - data-EDCB-main-image: main product image element.
      - data-EDCB-zoom: container used by jquery.zoom.
      - data-EDCB-thumb: thumbnail buttons with image metadata.
      - data-EDCB-swatch: swatch option buttons (if enabled).
      - data-EDCB-option-select: dropdown option selectors (if swatches disabled).
      - data-EDCB-form: add-to-cart form element.
      - data-EDCB-price / data-EDCB-compare: price targets.
      - data-EDCB-klarna: Klarna text element.
      - data-EDCB-klarna-button: button(s) that open the Klarna popup.
      - data-EDCB-klarna-modal: Klarna modal container.

    Section settings (dataset on root):
      - edcbInitialVariantId: initial variant fallback.
      - edcbZoomEnabled: enable/disable zoom.
      - edcbVariantsEnabled: enable/disable variant UI.
      - edcbSwatchesEnabled: swatches vs dropdowns.
      - edcbAtcText: custom ATC button text.
      - edcbKlarnaCustom: use custom Klarna text (richtext).
      - edcbKlarnaPopup: enable popup behavior.

    Behavior:
      - Syncs variant selection to price, availability, image, and Klarna text.
      - Re-initializes zoom after the main image updates.
      - Uses Klarna modal with overlay when enabled.
  */
        pendingZoomSrc = getZoomUrl(mainImage.currentSrc || mainImage.src);
        zoomInitialized = false;
        scheduleZoomInit();
      });
      observer.observe(mainImage, { attributes: true, attributeFilter: ['src', 'srcset'] });
    }
    scheduleZoomInit();
    if (zoomEnabled) {
      window.addEventListener('load', scheduleZoomInit, { once: true });
    }
    if (tabButtons.length) activateTab(tabButtons[0].dataset.tabTarget);
  });
})();




